import ConfigDefault from './../config.default.json';
import ConfigEnv from './../config.json';
import log from 'loglevel';
import type { LogLevelDesc } from 'loglevel';
import type { Language } from './LocaleService';

/**
 * Configuration shape.
 *
 * It is inferred from config.default.json, the config file checked into the
 * repository. src/config.json is the per-environment override that
 * scripts/build.sh copies into place.
 *
 * OJO: el override no solo cambia valores, tambien anade claves. Las que no
 * estan en config.default.json van declaradas aparte y son opcionales, porque
 * no hay garantia de que el entorno las traiga.
 */
export type AppConfig = typeof ConfigDefault & {
  /**
   * Secreto del cliente OAuth2. Solo lo llevan los clientes confidenciales
   * —las builds nativas, con client_id "maps_app"—; las de web no.
   * @see Oauth2Service.getConsumerData
   */
  oauth2_client_secret?: string;
};

declare global {
  interface Window {
    /** Configuration copied over for compatibility. @see Config.init */
    config: AppConfig;
  }
}

/**
 * Wrapper class to manage configuration.
 */
class Config {

  // Configuration values.
  static values: AppConfig;

  /**
   * Gets App name.
   */
  static getAppName(): string {
    return this.isProduction() ? "Gronze Maps" : "Gronze Test";
  }

  /**
   * Gets App tag (Beta)
   */
  static getAppTag(): string {
    return "";
  }

  /**
   * Gets App version.
   */
  static getAppVersion(): string {
    return import.meta.env.VITE_APP_VERSION;
  }

  /**
   * Gets App environment (production, testing, devel, local)
   */
  static getAppEnv(): string {
    return this.config().app_env;
  }

  /**
   * Gets build / node environment (development|production)
   */
  static getBuildEnv(): string {
    return import.meta.env.MODE;
  }

  /**
   * Time to recheck app version or authentication, in seconds.
   */
  static getRefreshTime(): number {
    return this.getBuildEnv() === 'production' ? this.config().app_refresh_time : 60;
  }

  /**
   * Gets REST URL.
   */
  static getRestUrl(): string {
      return this.config().rest_url;
  }

  /**
   * Gets oauth2 scope.
   */
  static getOauth2Scope(): string {
      return this.config().oauth2_scope;
  }

  /**
   * Gets base Site URL.
   */
  static getSiteUrl(): string {
    return this.config().rest_url;
  }

  /**
   * Gets report error destination email.
   */
  static getReportErrorEmail(): string {
    return this.config().report_error_email;
  }

  /**
   * Gets report error WhatsApp phone.
   */
  static getReportErrorWhatsappPhone(): string {
    return this.config().report_error_whatsapp_phone;
  }

  /**
   * Gets iOS App Store identifier used to open App Store listing.
   */
  static getIosAppStoreId(): string {
    return this.config().ios_app_store_id;
  }

  /**
   * Gets minimum number of consecutive days required to prompt app rating.
   */
  static getRateAppConsecutiveDays(): number {
    return this.config().rate_app_consecutive_days ?? 4;
  }

  /**
   * Gets the number of days to wait before showing the deferred rate dialog again.
   */
  static getRateAppDeferredDays(): number {
    return this.config().rate_app_deferred_days ?? 30;
  }

  /**
   * Gets site links.
   *
   * El idioma llega como parametro en vez de pedirlo a LocaleService porque
   * LocaleService ya importa Config: la importacion inversa cerraria un ciclo.
   *
   * @param lang Idioma de las paginas legales. La de ayuda solo existe en espanol.
   */
  static getSiteLinks(lang: Language) {
    const baseUrl = this.getSiteUrl();
    const legalPaths = lang === 'es'
      ? {
        legal_usage: '/legal/condiciones-uso',
        legal_privacy: '/legal/politica-privacidad',
        legal_cookies: '/legal/politica-cookies',
      }
      : {
        legal_usage: '/en/legal/terms-of-use',
        legal_privacy: '/en/legal/privacy-policy',
        legal_cookies: '/en/legal/cookie-policy',
      };

    return {
      help: baseUrl + '/gronze-maps',
      legal_usage: baseUrl + legalPaths.legal_usage,
      legal_privacy: baseUrl + legalPaths.legal_privacy,
      legal_cookies: baseUrl + legalPaths.legal_cookies,
      user_edit: baseUrl + '/user/'
    };
  }

  /**
   * Return TRUE if the Login is Required.
   */
  static isLoginRequired(): boolean {
    return this.config().app_login_required;
  }

  /**
   * Return TRUE if the we must to use analytics tracking.
   */
  static isAnalyticsEnabled(): boolean {
    return this.config().app_analytics_enabled;
  }

  /**
   * Return TRUE if analytics can use cookies (consent for analytics_storage).
   * When true, GA4 can use first-party cookies for session tracking, user identification, etc.
   */
  static isAnalyticsCookiesEnabled(): boolean {
    const cfg = this.config();
    return cfg.app_analytics_cookies_enabled !== false;
  }

  /**
   * Return TRUE if the Login is Allowed.
   */
  static isLoginAllowed(): boolean {
    // For now, if not required, not allowed either. This may change...
    return this.isLoginRequired();
  }

  // get icons fonts from the configured server
  static addIconFont(): void {
    const styles = "@import url('" + this.getSiteUrl() + "/sites/all/themes/gronze2016/css/icons.css');"
    const styleSheet = document.createElement("style")
    styleSheet.type = "text/css"
    styleSheet.innerText = styles
    document.head.appendChild(styleSheet)
  }

  /**
   * Get configuration values / stored / default config if not loaded.
   */
  static config(): AppConfig {
    if (!this.values) {
      this.init();
    }
    return this.values;
  }

  /**
   * Set configuration values. Init logging.
   *
   * @param config
   *   Configuration data.
   */
  static init(config?: Partial<AppConfig>): AppConfig {
    if (config) {
      this.values =  Object.assign(ConfigDefault, config);
    } else {
      // Not loaded, use default
      this.values =  Object.assign(ConfigDefault, ConfigEnv);
      // log.debug("Init config", this.values)
    }

    // Initialize log.
    this.initLogging(this.values);

    // Copy over to window config for compatibility
    window.config = this.values;

    // Initialize Icon fonts.
    this.addIconFont();

    return this.values
  }

  /**
   * Initialize logging
   */
  static initLogging(config: AppConfig): void {
    const level = localStorage.getItem('loglevel');

    // Neither the stored value nor the JSON key are validated anywhere, so the
    // level is asserted at this boundary. loglevel itself throws on a bad one.
    if (level) {
      log.setLevel(level as LogLevelDesc);
    }
    else {
      log.setLevel(config.app_loglevel as LogLevelDesc);
    }
  }

  /**
   * Check production version / env
   */
  static isProduction(): boolean {
    return this.getAppEnv() === 'production' && this.getBuildEnv() === 'production';
  }
}

export default Config;
